/**
 * Non-component exports for `MapListHeader` — split into a sibling
 * module so `MapListHeader.tsx` only exports the component itself
 * (react-refresh/only-export-components). Shared by the desktop
 * (`MapSidebar`) and mobile (`MapMobileLayout`) list headers so the
 * two call sites can't drift on sort-key labels or default
 * directions (CR-05, 2026-07-08 — Maria 3e).
 */
export type SortKey = 'name' | 'ratio' | 'credentials' | 'jobs' | 'shortage';

/**
 * Human-readable label per sort key, in the exact order the client
 * asked for: "allow sorting by credentials, job openings, and
 * shortage... make it a dropdown selection." Name and ratio (the
 * pre-existing options) stay first so the dropdown reads as an
 * extension of the old button, not a reshuffle.
 */
export const SORT_KEY_LABEL: Readonly<Record<SortKey, string>> = {
  name: 'Name',
  ratio: 'Credentials-to-jobs ratio',
  credentials: 'Annual credentials',
  jobs: 'Annual job openings',
  shortage: 'Annual credential shortage',
};

/** Order the dropdown's <option> elements render in. */
export const SORT_KEYS: ReadonlyArray<SortKey> = [
  'name',
  'ratio',
  'credentials',
  'jobs',
  'shortage',
];

/**
 * Sensible default sort DIRECTION per key, applied whenever the user
 * picks a new key from the dropdown. Ratio keeps its pre-existing
 * "worst first" default (ascending — lowest ratio = greatest shortage
 * at the top); the 3 new numeric keys default to "biggest first"
 * (descending), matching the reading order a researcher scanning for
 * the largest gaps would want. Name defaults to alphabetical
 * ascending.
 */
export const DEFAULT_SORT_ASC: Readonly<Record<SortKey, boolean>> = {
  name: true,
  ratio: true,
  credentials: false,
  jobs: false,
  shortage: false,
};
